import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { Order } from "../../../types";
import QRCode from "qrcode";

// Types
interface PrintSettings {
  mode: 'color' | 'bw';
  includeQR: boolean;
  showInstructions: boolean;
  showDeliveryInfo: boolean;
  qrSize: 'small' | 'medium' | 'large';
  paperSize: 'A4' | 'A5' | 'A6';
}

interface PrintOrderModalProps {
  order: Order;
  onClose: () => void;
  isOpen: boolean;
}

// Paper sizes in mm (width x height) - الدقة بدقة 0.1mm
const PAPER_SIZES = {
  A4: { width: 210.0, height: 297.0 },
  A5: { width: 148.0, height: 210.0 },
  A6: { width: 105.0, height: 148.0 }
};

// Calculate dimensions based on paper size and margins
const getPaperDimensions = (paperSize: 'A4' | 'A5' | 'A6') => {
  const size = PAPER_SIZES[paperSize];
  
  // تحديد الهوامش بناءً على حجم الورق (بالميلليمتر)
  let margin: number;
  if (paperSize === 'A6') {
    margin = 5.0; // هوامش صغيرة جداً لـ A6
  } else if (paperSize === 'A5') {
    margin = 7.0; // هوامش متوسطة لـ A5
  } else {
    margin = 8.0; // هوامش معقولة لـ A4
  }
  
  return {
    pageWidth: size.width,
    pageHeight: size.height,
    margin,
    contentWidth: size.width - (margin * 2),
    contentHeight: size.height - (margin * 2)
  };
};

// Utility functions
const generateWhatsAppUrl = (phone: string): string => {
  const cleanPhone = phone.replace(/[\s+\-()]/g, '');
  const whatsappNumber = cleanPhone.startsWith('0') ? cleanPhone.substring(1) : cleanPhone;
  return `https://wa.me/${whatsappNumber}`;
};

const generateDeliveryUrl = (orderId: number): string => {
  return `https://bazaar-marketplace.com/orders/${orderId}`;
};

const generateAddressText = (address: any): string => {
  if (!address) return '';
  const parts = [];
  if (address.building_or_house) parts.push(address.building_or_house);
  if (address.compound) parts.push(address.compound);
  if (address.street) parts.push(address.street);
  if (address.neighborhood) parts.push(address.neighborhood);
  if (address.city) parts.push(address.city);
  if (address.center) parts.push(address.center);
  return parts.join('، ');
};

const formatPhoneDisplay = (phone: string): string => {
  if (!phone) return '';
  return phone.replace(/(\d{3})(\d{4})(\d{3})/, '$1 $2 $3');
};

// Sub-components
const PrintSettingsPanel: React.FC<{
  settings: PrintSettings;
  onSettingsChange: (settings: PrintSettings) => void;
  t: (key: string) => string;
}> = ({ settings, onSettingsChange, t }) => {
  const updateSetting = (key: keyof PrintSettings, value: any) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Paper Size */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('print.paperSize') || 'Paper Size'}
        </label>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => updateSetting('paperSize', 'A4')}
            className={`px-3 py-2.5 rounded-lg border transition-colors duration-200 flex flex-col items-center ${
              settings.paperSize === 'A4'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-500 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <span className="text-base font-semibold">A4</span>
            <span className="text-xs mt-0.5 text-gray-600">210×297mm</span>
            <span className="text-[10px] mt-0.5 text-gray-500">صفحة كاملة</span>
          </button>
          <button
            onClick={() => updateSetting('paperSize', 'A5')}
            className={`px-3 py-2.5 rounded-lg border transition-colors duration-200 flex flex-col items-center ${
              settings.paperSize === 'A5'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-500 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <span className="text-base font-semibold">A5</span>
            <span className="text-xs mt-0.5 text-gray-600">148×210mm</span>
            <span className="text-[10px] mt-0.5 text-gray-500">نصف صفحة</span>
          </button>
          <button
            onClick={() => updateSetting('paperSize', 'A6')}
            className={`px-3 py-2.5 rounded-lg border transition-colors duration-200 flex flex-col items-center ${
              settings.paperSize === 'A6'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-500 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
            }`}
          >
            <span className="text-base font-semibold">A6</span>
            <span className="text-xs mt-0.5 text-gray-600">105×148mm</span>
            <span className="text-[10px] mt-0.5 text-gray-500">ملصق صغير</span>
          </button>
        </div>
      </div>

      {/* Color Mode */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('print.colorMode')}
        </label>
        <div className="flex gap-3">
          <button
            onClick={() => updateSetting('mode', 'color')}
            className={`flex-1 px-4 py-2 rounded-lg border transition-colors duration-200 flex items-center justify-center gap-2 ${
              settings.mode === 'color'
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border-green-500 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span>🎨</span>
            {t('print.color')}
          </button>
          <button
            onClick={() => updateSetting('mode', 'bw')}
            className={`flex-1 px-4 py-2 rounded-lg border transition-colors duration-200 flex items-center justify-center gap-2 ${
              settings.mode === 'bw'
                ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white border-gray-700 shadow-sm'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span>⚫</span>
            {t('print.blackWhite')}
          </button>
        </div>
      </div>

      {/* QR Size */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('print.qrSize')}
        </label>
        <select
          value={settings.qrSize}
          onChange={(e) => updateSetting('qrSize', e.target.value as any)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
        >
          <option value="small">{t('print.small')} - صغير</option>
          <option value="medium">{t('print.medium')} - متوسط</option>
          <option value="large">{t('print.large')} - كبير</option>
        </select>
      </div>

      {/* Options */}
      <div className="space-y-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <label className="flex items-center justify-between gap-3 p-2 bg-white rounded-md hover:bg-gray-50 transition-colors cursor-pointer">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <div className={`w-4 h-4 border-2 rounded ${settings.includeQR ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {settings.includeQR && <div className="w-2 h-2 bg-white mx-auto mt-0.5"></div>}
              </div>
            </div>
            <span className="text-sm text-gray-700 font-medium">
              {t('print.includeQR')}
            </span>
          </div>
          <input
            type="checkbox"
            checked={settings.includeQR}
            onChange={(e) => updateSetting('includeQR', e.target.checked)}
            className="hidden"
          />
        </label>

        <label className="flex items-center justify-between gap-3 p-2 bg-white rounded-md hover:bg-gray-50 transition-colors cursor-pointer">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <div className={`w-4 h-4 border-2 rounded ${settings.showDeliveryInfo ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {settings.showDeliveryInfo && <div className="w-2 h-2 bg-white mx-auto mt-0.5"></div>}
              </div>
            </div>
            <span className="text-sm text-gray-700 font-medium">
              {t('print.showDeliveryInfo')}
            </span>
          </div>
          <input
            type="checkbox"
            checked={settings.showDeliveryInfo}
            onChange={(e) => updateSetting('showDeliveryInfo', e.target.checked)}
            className="hidden"
          />
        </label>

        <label className="flex items-center justify-between gap-3 p-2 bg-white rounded-md hover:bg-gray-50 transition-colors cursor-pointer">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <div className={`w-4 h-4 border-2 rounded ${settings.showInstructions ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                {settings.showInstructions && <div className="w-2 h-2 bg-white mx-auto mt-0.5"></div>}
              </div>
            </div>
            <span className="text-sm text-gray-700 font-medium">
              {t('print.showInstructions')}
            </span>
          </div>
          <input
            type="checkbox"
            checked={settings.showInstructions}
            onChange={(e) => updateSetting('showInstructions', e.target.checked)}
            className="hidden"
          />
        </label>
      </div>

      {/* Print Info */}
      <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 text-sm">ℹ️</span>
          </div>
          <h4 className="font-medium text-blue-700">
            {t('print.printGuide') || 'إرشادات الطباعة'}
          </h4>
        </div>
        <ul className="text-xs text-blue-600 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>{t('print.guide1') || 'تأكد من تعبئة الحبر في الطابعة'}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>{t('print.guide2') || 'اختر حجم الورق المناسب'}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>{t('print.guide3') || 'تأكد من ضبط الهوامش'}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>{t('print.guide4') || 'اختبر الطباعة على صفحة واحدة أولاً'}</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

// Main Component
const PrintOrderModal: React.FC<PrintOrderModalProps> = ({
  order,
  onClose,
  isOpen,
}) => {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState<PrintSettings>({
    mode: 'color',
    includeQR: true,
    showInstructions: true,
    showDeliveryInfo: true,
    qrSize: 'medium',
    paperSize: 'A4'
  });
  
  const [isPrinting, setIsPrinting] = useState(false);
  const isRTL = i18n.language === 'ar';

  // Get phone number
  const phoneNumber = order.address?.mobile || '01503323006';

  // Calculate QR size based on paper size - دقة بالبكسل
  const getQrSize = (): number => {
    const baseSize = settings.qrSize;
    const paperScale = {
      A4: 1.0,
      A5: 0.65, // أصغر بنسبة 35%
      A6: 0.45  // أصغر بنسبة 55%
    };
    
    const scale = paperScale[settings.paperSize];
    
    switch (baseSize) {
      case 'small': return Math.round(90 * scale);   // A4: 90px, A5: 59px, A6: 41px
      case 'large': return Math.round(160 * scale);  // A4: 160px, A5: 104px, A6: 72px
      default: return Math.round(125 * scale);       // A4: 125px, A5: 81px, A6: 56px
    }
  };

  // Generate URLs
  const whatsappUrl = useMemo(() => generateWhatsAppUrl(phoneNumber), [phoneNumber]);
  const deliveryUrl = useMemo(() => generateDeliveryUrl(order.id), [order.id]);

  // Get paper dimensions
  const getPaperConfig = () => {
    const dimensions = getPaperDimensions(settings.paperSize);
    return dimensions;
  };

  // Get optimized font sizes based on paper size
  const getOptimizedFontSizes = () => {
    switch (settings.paperSize) {
      case 'A6':
        return {
          title: '13px',
          subtitle: '11px',
          body: '9px',
          small: '8px',
          tiny: '7px',
          qrLabel: '10px',
          icon: '1.1rem'
        };
      case 'A5':
        return {
          title: '15px',
          subtitle: '12px',
          body: '10px',
          small: '9px',
          tiny: '8px',
          qrLabel: '11px',
          icon: '1.3rem'
        };
      case 'A4':
      default:
        return {
          title: '17px',
          subtitle: '14px',
          body: '12px',
          small: '10px',
          tiny: '9px',
          qrLabel: '13px',
          icon: '1.5rem'
        };
    }
  };

  // Get spacing based on paper size
  const getSpacing = () => {
    switch (settings.paperSize) {
      case 'A6':
        return {
          gap: '0.25rem',    // 4px
          padding: '0.4rem', // 6.4px
          margin: '0.3rem',  // 4.8px
          borderRadius: '0.25rem'
        };
      case 'A5':
        return {
          gap: '0.375rem',   // 6px
          padding: '0.5rem', // 8px
          margin: '0.4rem',  // 6.4px
          borderRadius: '0.375rem'
        };
      case 'A4':
      default:
        return {
          gap: '0.5rem',     // 8px
          padding: '0.75rem', // 12px
          margin: '0.5rem',  // 8px
          borderRadius: '0.5rem'
        };
    }
  };

  // Direct print function - محسنة للمسافات
  const handlePrint = async (): Promise<void> => {
    if (!settings.includeQR) {
      alert(t('print.includeQRWarning') || 'Please enable QR codes to print');
      return;
    }

    setIsPrinting(true);
    
    try {
      // الخطوة 1: جمع البيانات الأساسية
      const qrSize = getQrSize();
      const isColorMode = settings.mode === 'color';
      const paperConfig = getPaperConfig();
      const fontSizes = getOptimizedFontSizes();
      const spacing = getSpacing();
      
      // الخطوة 2: توليد QR Codes بصيغة Data URL
      const whatsappQrDataUrl = await QRCode.toDataURL(whatsappUrl, {
        width: qrSize,
        margin: 2, // زيادة الهامش قليلاً ليكون أوضح
        color: {
          dark: isColorMode ? '#25D366' : '#000000',
          light: '#FFFFFF'
        }
      });

      const deliveryQrDataUrl = await QRCode.toDataURL(deliveryUrl, {
        width: qrSize,
        margin: 2,
        color: {
          dark: isColorMode ? '#667eea' : '#000000',
          light: '#FFFFFF'
        }
      });

      // الخطوة 3: إنشاء HTML للطباعة
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert(t('print.printError') || 'Could not open print window. Please allow popups.');
        return;
      }

      // محتوى الطباعة المحسن للمسافات
      const printContent = `
        <!DOCTYPE html>
        <html lang="${i18n.language}" dir="${isRTL ? 'rtl' : 'ltr'}">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${t('print.deliveryLabel', { orderId: order.id })} | Bazaar Marketplace</title>
          <style>
            /* إعدادات حسب حجم الورق - محسنة للمسافات */
            @page {
              size: ${paperConfig.pageWidth.toFixed(1)}mm ${paperConfig.pageHeight.toFixed(1)}mm;
              margin: ${paperConfig.margin.toFixed(1)}mm;
            }
            
            @media print {
              /* إعادة تعيين كاملة */
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              
              body {
                background: white !important;
                color: ${isColorMode ? '#333' : '#000'} !important;
                font-family: 'Segoe UI', 'Arial', 'Tahoma', 'sans-serif';
                font-size: ${fontSizes.body};
                line-height: 1.3;
                width: ${paperConfig.pageWidth.toFixed(1)}mm;
                height: ${paperConfig.pageHeight.toFixed(1)}mm;
                margin: 0 auto;
                padding: 0;
              }
              
              /* الحاوية الرئيسية - مضبوطة المسافات */
              .print-container {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                gap: ${spacing.gap};
                padding: ${paperConfig.margin.toFixed(1)}mm;
                ${!isColorMode ? 'filter: grayscale(100%);' : ''}
              }
              
              /* الهيدر - مسافات مضبوطة */
              .print-header {
                text-align: center;
                padding-bottom: ${spacing.padding};
                margin-bottom: ${spacing.margin};
                border-bottom: 1px solid #e0e0e0;
              }
              
              .company-name {
                font-size: ${fontSizes.title};
                font-weight: 700;
                color: ${isColorMode ? '#1a202c' : '#000'};
                margin-bottom: 0.1rem;
                letter-spacing: 0.5px;
              }
              
              .company-tagline {
                font-size: ${fontSizes.small};
                color: #666;
                margin-bottom: 0.2rem;
              }
              
              .document-title {
                font-size: ${fontSizes.subtitle};
                font-weight: 600;
                color: #2d3748;
              }
              
              /* معلومات الطلب - مسافات مضبوطة */
              .order-info {
                background: ${isColorMode ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)'};
                color: white;
                padding: ${spacing.padding};
                border-radius: ${spacing.borderRadius};
                margin-bottom: ${spacing.margin};
              }
              
              .order-id {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                margin-bottom: 0.3rem;
              }
              
              .order-status {
                background: rgba(255, 255, 255, 0.15);
                padding: 0.15rem 0.5rem;
                border-radius: 9999px;
                font-size: ${fontSizes.tiny};
                font-weight: 500;
              }
              
              /* معلومات التسليم - مسافات مضبوطة */
              .delivery-info {
                border: 1px solid #d0d5dd;
                border-radius: ${spacing.borderRadius};
                padding: ${spacing.padding};
                background: #f8fafc;
                margin-bottom: ${spacing.margin};
              }
              
              .delivery-title {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                margin-bottom: 0.4rem;
              }
              
              /* شبكة QR - مسافات مضبوطة */
              .qr-grid {
                display: ${settings.paperSize === 'A6' ? 'flex' : 'grid'};
                flex-direction: ${settings.paperSize === 'A6' ? 'column' : 'row'};
                ${settings.paperSize !== 'A6' ? `grid-template-columns: ${settings.paperSize === 'A5' ? '1fr' : '1fr 1fr'};` : ''}
                gap: ${spacing.gap};
                margin: ${spacing.margin} 0;
              }
              
              .qr-item {
                border: 1px solid #e2e8f0;
                border-radius: ${spacing.borderRadius};
                padding: ${spacing.padding};
                text-align: center;
                background: white;
                flex: 1;
              }
              
              .qr-icon {
                font-size: ${fontSizes.icon};
                margin-bottom: 0.3rem;
              }
              
              .qr-image-container {
                margin: 0.5rem auto;
                padding: 0.25rem;
                border: 1px solid #e5e7eb;
                border-radius: 0.375rem;
                background: white;
                width: ${qrSize + 8}px;
                height: ${qrSize + 8}px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              
              .qr-image {
                width: ${qrSize}px;
                height: ${qrSize}px;
                display: block;
              }
              
              .qr-title {
                font-size: ${fontSizes.qrLabel};
                font-weight: 600;
                color: #1f2937;
                margin-bottom: 0.2rem;
              }
              
              .qr-description {
                font-size: ${fontSizes.small};
                color: #6b7280;
                line-height: 1.25;
                margin-bottom: 0.3rem;
              }
              
              /* التعليمات - مسافات مضبوطة */
              .instructions {
                border: 1px dashed ${isColorMode ? '#ef4444' : '#9ca3af'};
                border-radius: ${spacing.borderRadius};
                padding: ${spacing.padding};
                background: ${isColorMode ? '#fef2f2' : '#f8fafc'};
                margin-bottom: ${spacing.margin};
              }
              
              .instructions-title {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                font-size: ${fontSizes.subtitle};
                font-weight: 600;
                color: ${isColorMode ? '#991b1b' : '#374151'};
                margin-bottom: 0.4rem;
              }
              
              .instructions-grid {
                display: grid;
                grid-template-columns: ${settings.paperSize === 'A6' ? '1fr' : '1fr 1fr'};
                gap: 0.4rem;
              }
              
              .instruction-item {
                display: flex;
                align-items: flex-start;
                gap: 0.4rem;
              }
              
              .instruction-number {
                flex-shrink: 0;
                width: 1.25rem;
                height: 1.25rem;
                border-radius: 9999px;
                background: ${isColorMode ? '#fecaca' : '#e5e7eb'};
                color: ${isColorMode ? '#dc2626' : '#6b7280'};
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${fontSizes.tiny};
                font-weight: 600;
              }
              
              /* الفوتر - مسافات مضبوطة */
              .print-footer {
                text-align: center;
                margin-top: auto;
                padding-top: ${spacing.padding};
                border-top: 1px solid #e5e7eb;
                color: #6b7280;
                font-size: ${fontSizes.tiny};
              }
              
              /* العلامة المائية */
              .watermark {
                opacity: 0.03;
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-45deg);
                font-size: ${settings.paperSize === 'A6' ? '2.5rem' : settings.paperSize === 'A5' ? '3.5rem' : '4.5rem'};
                color: #000;
                z-index: -1;
                pointer-events: none;
                font-weight: 900;
                white-space: nowrap;
                letter-spacing: 2px;
              }
              
              /* إخفاء العناصر غير المطلوبة */
              .no-print {
                display: none !important;
              }
              
              /* منع تقسيم العناصر */
              .qr-grid,
              .qr-item,
              .order-info,
              .delivery-info,
              .instructions {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
            }
            
            /* قبل الطباعة */
            body {
              font-family: 'Segoe UI', 'Arial', 'Tahoma', sans-serif;
              background: white;
              margin: 0;
              padding: 0;
            }
            
            .pre-print-message {
              text-align: center;
              padding: 3rem;
              color: #4b5563;
              font-size: 1.125rem;
              line-height: 1.6;
            }
            
            .print-container {
              display: none;
            }
            
            @media screen {
              .pre-print-message {
                display: block;
              }
              .print-container {
                display: none;
              }
            }
            
            @media print {
              .pre-print-message {
                display: none;
              }
              .print-container {
                display: flex !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="pre-print-message no-print">
            <h2 style="margin-bottom: 1rem; color: #1f2937;">${t('print.preparingPrint') || 'جارٍ تجهيز الطباعة...'}</h2>
            <p style="margin-bottom: 0.5rem;">${t('print.printWillStart') || 'ستفتح نافذة الطباعة تلقائياً'}</p>
            <p style="font-size: 0.875rem; color: #6b7280; margin-top: 1rem;">
              حجم الورق: ${settings.paperSize} (${paperConfig.pageWidth.toFixed(0)}×${paperConfig.pageHeight.toFixed(0)}mm)
            </p>
          </div>
          
          <!-- الحاوية الرئيسية للطباعة -->
          <div class="print-container">
            <!-- الهيدر -->
            <div class="print-header">
              <div class="company-name">Bazaar Marketplace</div>
              <div class="company-tagline">خدمة توصيل متميزة</div>
              <div class="document-title">${t('print.deliveryDocument') || 'مستند التوصيل'}</div>
            </div>

            <!-- معلومات الطلب -->
            <div class="order-info">
              <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                <div>
                  <div class="order-id">
                    <div style="font-size: ${fontSizes.subtitle}; font-weight: 600;">
                      ${t('print.orderLabel') || 'طلب'} #${order.id}
                    </div>
                    <span class="order-status">
                      ${t(`orderStatus.${order.status}`) || order.status}
                    </span>
                  </div>
                  <div style="font-size: ${fontSizes.small}; opacity: 0.9;">
                    ${new Date(order.created_at).toLocaleDateString('ar-SA', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                
                <div style="text-align: ${isRTL ? 'left' : 'right'};">
                  <div style="font-size: ${fontSizes.subtitle}; font-weight: 600;">
                    ${order.total} ${t('print.currency') || 'ر.س'}
                  </div>
                  <div style="font-size: ${fontSizes.small}; opacity: 0.9;">
                    ${order.items.length} ${t('print.items') || 'منتج'}
                  </div>
                </div>
              </div>
            </div>

            <!-- معلومات التسليم -->
            ${settings.showDeliveryInfo ? `
              <div class="delivery-info">
                <div class="delivery-title">
                  <span style="font-size: ${fontSizes.icon};">📍</span>
                  <div style="font-size: ${fontSizes.body}; font-weight: 600; color: #1f2937;">
                    ${t('print.deliveryTo') || 'التوصيل إلى'}
                  </div>
                </div>
                
                <div style="display: grid; grid-template-columns: ${settings.paperSize === 'A6' ? '1fr' : '1fr 1fr'}; gap: ${spacing.gap};">
                  <div>
                    <div style="font-size: ${fontSizes.small}; color: #6b7280; margin-bottom: 0.2rem;">
                      ${t('print.phoneNumber') || 'رقم الهاتف'}
                    </div>
                    <div style="font-size: ${fontSizes.subtitle}; font-weight: 600; color: #1f2937; direction: ltr; text-align: ${isRTL ? 'right' : 'left'};">
                      ${formatPhoneDisplay(phoneNumber)}
                    </div>
                    <div style="font-size: ${fontSizes.tiny}; color: #6b7280; margin-top: 0.2rem; display: flex; align-items: center; gap: 0.2rem;">
                      <span>📱</span>
                      ${t('print.contactForDelivery') || 'للتواصل بشأن التوصيل'}
                    </div>
                  </div>
                  
                  <div>
                    <div style="font-size: ${fontSizes.small}; color: #6b7280; margin-bottom: 0.2rem;">
                      ${t('print.deliveryAddress') || 'عنوان التوصيل'}
                    </div>
                    <div style="font-size: ${fontSizes.body}; font-weight: 500; color: #1f2937; line-height: 1.3;">
                      ${order.address ? generateAddressText(order.address) || t('print.noAddress') : t('print.noAddress') || 'لا يوجد عنوان'}
                    </div>
                  </div>
                </div>
              </div>
            ` : ''}

            <!-- رموز QR -->
            ${settings.includeQR ? `
              <div class="qr-grid">
                <!-- رمز واتساب -->
                <div class="qr-item">
                  <div class="qr-icon">💬</div>
                  <div class="qr-title">${t('print.whatsappQR') || 'رمز واتساب'}</div>
                  
                  <div class="qr-image-container">
                    <img 
                      src="${whatsappQrDataUrl}" 
                      alt="WhatsApp QR Code"
                      class="qr-image"
                      crossorigin="anonymous"
                    />
                  </div>
                  
                  <div class="qr-description">
                    ${t('print.whatsappDesc') || 'امسح الرمز للتواصل مع العميل عبر واتساب'}
                  </div>
                  
                  <div style="padding: 0.2rem 0.6rem; background: #d1fae5; color: #065f46; border-radius: 9999px; font-size: ${fontSizes.tiny}; display: inline-block; font-weight: 500;">
                    ${formatPhoneDisplay(phoneNumber)}
                  </div>
                </div>
                
                <!-- رمز التوصيل -->
                <div class="qr-item">
                  <div class="qr-icon">📦</div>
                  <div class="qr-title">${t('print.deliveryQR') || 'رمز التوصيل'}</div>
                  
                  <div class="qr-image-container">
                    <img 
                      src="${deliveryQrDataUrl}" 
                      alt="Delivery QR Code"
                      class="qr-image"
                      crossorigin="anonymous"
                    />
                  </div>
                  
                  <div class="qr-description">
                    ${t('print.deliveryDesc') || 'امسح الرمز لعرض تفاصيل الطلب والتوصيل'}
                  </div>
                  
                  <div style="padding: 0.2rem 0.6rem; background: #dbeafe; color: #1e40af; border-radius: 9999px; font-size: ${fontSizes.tiny}; display: inline-block; font-weight: 500;">
                    ${t('print.order') || 'طلب'} #${order.id}
                  </div>
                </div>
              </div>
            ` : ''}

            <!-- التعليمات -->
            ${settings.showInstructions ? `
              <div class="instructions">
                <div class="instructions-title">
                  <span>⚠️</span>
                  <div>${t('print.importantInstructions') || 'تعليمات مهمة'}</div>
                </div>
                
                <div class="instructions-grid">
                  <div>
                    ${[1, 2, 3].map(num => `
                      <div class="instruction-item">
                        <div class="instruction-number">${num}</div>
                        <div style="font-size: ${fontSizes.small}; color: #374151;">${t(`print.instruction${num}`)}</div>
                      </div>
                    `).join('')}
                  </div>
                  
                  ${settings.paperSize !== 'A6' ? `
                    <div>
                      ${[4, 5, 6].map(num => `
                        <div class="instruction-item">
                          <div class="instruction-number">${num}</div>
                          <div style="font-size: ${fontSizes.small}; color: #374151;">${t(`print.instruction${num}`)}</div>
                        </div>
                      `).join('')}
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}

            <!-- الفوتر -->
            <div class="print-footer">
              <div style="margin-bottom: 0.2rem;">${t('print.footerNote1') || 'هذا المستند خاص بتوصيل الطلبات'}</div>
              <div>
                ${t('print.footerNote2') || 'تم إنشاؤه بواسطة'} • ${new Date().toLocaleDateString('ar-SA')} ${new Date().toLocaleTimeString('ar-SA', {hour: '2-digit', minute:'2-digit'})}
              </div>
            </div>

            <!-- العلامة المائية -->
            <div class="watermark">
              ORDER #${order.id}
            </div>
          </div>

          <script>
            window.onload = function() {
              const images = document.querySelectorAll('img');
              let loadedCount = 0;
              
              const startPrint = () => {
                setTimeout(() => {
                  window.print();
                  setTimeout(() => {
                    window.close();
                  }, 1000);
                }, 400);
              };
              
              if (images.length === 0) {
                startPrint();
                return;
              }
              
              images.forEach(img => {
                if (img.complete) {
                  loadedCount++;
                } else {
                  img.onload = img.onerror = () => {
                    loadedCount++;
                    if (loadedCount === images.length) {
                      startPrint();
                    }
                  };
                }
              });
              
              if (loadedCount === images.length) {
                startPrint();
              }
            };
          </script>
        </body>
        </html>
      `;

      // فتح النافذة وكتابة المحتوى
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

    } catch (error) {
      console.error('Error printing:', error);
      alert(t('print.printError') || 'Error preparing print. Please try again.');
    } finally {
      setIsPrinting(false);
    }
  };

  // Preview QR codes in modal
  const renderPreviewQR = (type: 'whatsapp' | 'delivery') => {
    const qrSize = getQrSize();
    const value = type === 'whatsapp' ? whatsappUrl : deliveryUrl;
    const fgColor = settings.mode === 'color' 
      ? (type === 'whatsapp' ? '#25D366' : '#667eea') 
      : '#000000';

    return (
      <div className="flex flex-col items-center p-4 border rounded-lg bg-white shadow-sm">
        <div className="text-4xl mb-3">{type === 'whatsapp' ? '💬' : '📦'}</div>
        <h4 className="font-bold text-gray-800 mb-2 text-center">
          {type === 'whatsapp' ? t('print.whatsappQR') : t('print.deliveryQR')}
        </h4>
        <div className="mb-3 p-2 border border-gray-200 rounded-lg bg-white flex items-center justify-center">
          <QRCodeCanvas
            value={value}
            size={qrSize}
            fgColor={fgColor}
            bgColor="#ffffff"
            level="H"
            includeMargin={true}
          />
        </div>
        <p className="text-xs text-gray-600 text-center leading-relaxed max-w-[250px]">
          {type === 'whatsapp' ? t('print.whatsappDesc') : t('print.deliveryDesc')}
        </p>
        {type === 'whatsapp' && (
          <div className="mt-2 px-3 py-1 bg-green-50 text-green-700 text-xs rounded-full">
            {formatPhoneDisplay(phoneNumber)}
          </div>
        )}
        {type === 'delivery' && (
          <div className="mt-2 px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
            Order #{order.id}
          </div>
        )}
      </div>
    );
  };

  // Preview paper size in modal
  const renderPaperPreview = () => {
    const paperConfig = getPaperConfig();
    const scale = settings.paperSize === 'A4' ? 0.25 : settings.paperSize === 'A5' ? 0.35 : 0.45;
    
    return (
      <div className="mt-6">
        <div className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <span>📐</span>
          <span>{t('print.paperPreview') || 'معاينة حجم الورق'}</span>
        </div>
        <div className="flex justify-center p-3 bg-gray-50 rounded-lg">
          <div 
            className="border-2 border-blue-300 bg-gradient-to-b from-white to-blue-50 flex flex-col items-center justify-center relative shadow-sm"
            style={{
              width: `${paperConfig.pageWidth * scale}px`,
              height: `${paperConfig.pageHeight * scale}px`,
              margin: '0 auto'
            }}
          >
            <div className="text-sm font-bold text-blue-700">
              {settings.paperSize}
            </div>
            <div className="text-xs text-blue-600 mt-1 font-medium">
              ${paperConfig.pageWidth.toFixed(0)}×${paperConfig.pageHeight.toFixed(0)}mm
            </div>
            <div className="absolute top-1 left-1 w-2 h-2 bg-blue-400 rounded-full"></div>
            <div className="absolute top-1 right-1 w-2 h-2 bg-blue-400 rounded-full"></div>
            <div className="absolute bottom-1 left-1 w-2 h-2 bg-blue-400 rounded-full"></div>
            <div className="absolute bottom-1 right-1 w-2 h-2 bg-blue-400 rounded-full"></div>
            
            {settings.paperSize === 'A6' && (
              <div className="text-[10px] text-gray-500 mt-3 px-2 py-1 bg-white/70 rounded">حجم ملصق</div>
            )}
            {settings.paperSize === 'A5' && (
              <div className="text-[10px] text-gray-500 mt-3 px-2 py-1 bg-white/70 rounded">نصف صفحة</div>
            )}
            {settings.paperSize === 'A4' && (
              <div className="text-[10px] text-gray-500 mt-3 px-2 py-1 bg-white/70 rounded">صفحة كاملة</div>
            )}
          </div>
        </div>
        <div className="text-xs text-gray-500 text-center mt-2">
          المساحة الفعلية: ${((paperConfig.contentWidth * paperConfig.contentHeight) / 100).toFixed(1)} سم²
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {t('print.printDeliveryLabel') || 'طباعة ملصق التوصيل'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              ${t('print.order') || 'طلب'} #${order.id} • ${t('print.bazaarMarketplace') || 'بازار ماركت'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg transition-colors duration-200 hover:shadow-sm"
            aria-label={t('common.close') || 'إغلاق'}
          >
            <span className="text-gray-600 text-xl hover:text-gray-800">✕</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex">
          {/* Settings Panel */}
          <div className="w-1/3 border-r p-6 overflow-y-auto bg-gray-50">
            <div className="sticky top-0">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-blue-500">⚙️</span>
                {t('print.printSettings') || 'إعدادات الطباعة'}
              </h3>
              <PrintSettingsPanel
                settings={settings}
                onSettingsChange={setSettings}
                t={t}
              />
              {renderPaperPreview()}
              
              {/* Paper Info */}
              <div className="mt-6 p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-blue-500">📋</span>
                  <div className="text-sm font-medium text-gray-700">معلومات الطباعة</div>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>حجم الورق:</span>
                    <span className="font-medium">${settings.paperSize}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>نوع الطباعة:</span>
                    <span className="font-medium">${settings.mode === 'color' ? 'ملون' : 'أسود وأبيض'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>حجم QR:</span>
                    <span className="font-medium">${settings.qrSize === 'small' ? 'صغير' : settings.qrSize === 'large' ? 'كبير' : 'متوسط'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>عدد العناصر:</span>
                    <span className="font-medium">${order.items.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="w-2/3 p-6 overflow-y-auto">
            <div className="sticky top-0 bg-white pb-4 mb-4 border-b z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <span className="text-green-500">👁️</span>
                  {t('print.preview') || 'معاينة'}
                </h3>
                <div className="text-sm text-gray-600 bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-1.5 rounded-lg border border-blue-100">
                  <span className="font-medium">${settings.paperSize}</span> • 
                  <span className="mx-2">|</span>
                  <span className="font-medium">${settings.qrSize === 'small' ? 'QR صغير' : settings.qrSize === 'large' ? 'QR كبير' : 'QR متوسط'}</span> • 
                  <span className="mx-2">|</span>
                  <span className="font-medium">${settings.mode === 'color' ? 'ملون' : 'أسود'}</span>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                {t('print.previewDescription') || 'معاينة قبل الطباعة - ستظهر الصفحة كاملة في الطباعة'}
              </p>
            </div>
            
            <div className="border-2 border-gray-300 rounded-xl p-4 bg-white shadow-inner">
              <div className="scale-75 origin-top mx-auto" style={{ maxWidth: '210mm' }}>
                <div className="p-8 bg-white" style={{ width: '210mm', minHeight: '297mm' }}>
                  {settings.includeQR && (
                    <div className="grid grid-cols-2 gap-8 my-8">
                      {renderPreviewQR('whatsapp')}
                      {renderPreviewQR('delivery')}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-center mt-6 pt-4 border-t">
                <div className="text-sm text-gray-500 inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg">
                  <span>📄</span>
                  {t('print.scaleNote') || 'المعاينة بمقياس 75% - ستكون الطباعة بحجمها الطبيعي'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              <div className="font-medium">{t('print.printReady') || 'جاهز للطباعة'}</div>
              <div className="text-xs text-gray-500 mt-1">
                ${order.items.length} ${t('print.items') || 'عنصر'} • ${t('print.total') || 'المجموع'}: ${order.total} ${t('print.currency') || 'ر.س'}
              </div>
              <div className="text-xs text-blue-600 mt-1 font-medium flex items-center gap-2">
                <span>📏 {settings.paperSize}</span>
                <span>•</span>
                <span>${settings.mode === 'color' ? '🎨 ملون' : '⚫ أسود وأبيض'}</span>
                <span>•</span>
                <span>🔳 ${settings.qrSize === 'small' ? 'QR صغير' : settings.qrSize === 'large' ? 'QR كبير' : 'QR متوسط'}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isPrinting}
              className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm"
            >
              <span>❌</span>
              {t('common.cancel') || 'إلغاء'}
            </button>
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-200 font-medium flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPrinting ? (
                <>
                  <span className="animate-spin">⏳</span>
                  {t('print.preparingPrint') || 'جارٍ التحضير...'}
                </>
              ) : (
                <>
                  <span className="text-xl">🖨️</span>
                  ${t('print.printNow') || `طباعة (${settings.paperSize})`}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintOrderModal;